import { useState } from "react";
import { postAgentRoomAction, type QueueItemView } from "../api";
import { toneClass, toneTextClass } from "./queueStateStyling";

/**
 * OWNEXP Slice 8 — Interactive Operator Controls.
 *
 * KEINE Fake-Buttons: jede hier sichtbare Aktion ist eine echte, serverseitig
 * autorisierte Agent-Room-Action (POST /api/agent-room/actions →
 * applyAgentRoomAction mit Fencing/Evidence). Die fachlichen Regeln liegen
 * ausschließlich auf dem Server; diese Fläche leitet nur die Voraussetzungen
 * aus demselben Read-Model ab (Projektionstreue), zeigt je Aktion Wirkung und
 * Risiko und holt bei destruktiven Aktionen eine explizite Bestätigung ein.
 * Ergebnisse erscheinen als Audit-Beleg (Evidence-Art + Zeitpunkt), Fehler
 * fail-closed mit übersetztem Server-Grund. Keine freie System-Shell.
 */

const PRIO_WAHL: Array<{ wert: string; label: string }> = [
  { wert: "urgent", label: "Dringend" },
  { wert: "high", label: "Hoch" },
  { wert: "normal", label: "Normal" },
  { wert: "low", label: "Niedrig" },
];

const FEHLER_TEXT: Record<string, string> = {
  workitem_terminal: "Item ist bereits abgeschlossen (terminal) — diese Aktion ist nicht mehr möglich.",
  not_wait_owner: "Das Item wartet nicht auf eine Owner-Entscheidung.",
  owner_role_required: "Diese Aktion erfordert die Owner-Rolle.",
  workitem_not_found: "Item existiert nicht (mehr).",
  no_active_recovery: "Für dieses Item läuft keine Recovery — Aktion abgelehnt.",
  challenge_required_before_verify: "Vor der Verifikation ist eine unabhängige Challenge erforderlich.",
  challenge_response_required_before_verify: "Vor der Verifikation fehlt die evidenzgebundene Challenge-Antwort.",
  claim_required: "Bitte einen Prüf-Claim (Behauptung) formulieren.",
  invalid_action_payload: "Ungültige Aktion — Server hat abgelehnt.",
};

const AUDIT_LABEL: Record<string, string> = {
  park: "agent_room: geparkt",
  resume: "agent_room: fortgesetzt",
  priority: "agent_room: Priorität geändert",
  safe_cancel: "agent_room: sicher abgebrochen",
  owner_decision: "agent_room: Gate-Entscheidung",
  challenge: "agent_room: Prüfung beauftragt",
  advance_recovery: "agent_room: Recovery-Schritt",
};

export function OperatorActionControls({ item, onAenderung }: { item: QueueItemView; onAenderung: () => void }): JSX.Element {
  const [laufend, setLaufend] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<{ action: string; text: string } | null>(null);
  const [fehler, setFehler] = useState<{ action: string; text: string } | null>(null);
  const [bestaetigung, setBestaetigung] = useState<"safe_cancel" | "resume_failed" | "reject" | null>(null);
  const [grund, setGrund] = useState("");
  const [prioWahl, setPrioWahl] = useState(item.priority);
  const [challengeClaim, setChallengeClaim] = useState("");
  const [challengeOffen, setChallengeOffen] = useState(false);

  const terminal = item.queueState === "COMPLETED" || item.status === "done";
  const wartetAufOwner = item.queueState === "WAIT_OWNER";
  const geparkt = item.queueState === "PARKED";
  const fehlerhaft = item.queueState === "FAILED";

  const ausfuehren = async (action: string, payload: Parameters<typeof postAgentRoomAction>[0]): Promise<void> => {
    setLaufend(action);
    setFehler(null);
    setReceipt(null);
    try {
      const result = await postAgentRoomAction(payload);
      if (result.ok) {
        setReceipt({ action, text: auditText(action, result.detail ?? {}) });
      } else {
        setFehler({ action, text: FEHLER_TEXT[result.error ?? ""] ?? `Server lehnte ab (${result.error ?? "unbekannt"}).` });
      }
    } catch (err) {
      setFehler({ action, text: `Nicht ausgeführt — Verbindung/Server: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setLaufend(null);
      setBestaetigung(null);
      setGrund("");
      setChallengeOffen(false);
      setChallengeClaim("");
      onAenderung();
    }
  };

  return (
    <div className="rounded-lg border border-jarvis-accent/20 bg-jarvis-accent/5 p-3" data-testid="operator-action-controls">
      <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Aktionen (serverseitig autorisiert, jede mit Audit-Beleg)</div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {/* Parkieren — reversibel, keine Bestätigung nötig */}
        <Aktion
          testid="action-park"
          disabled={terminal || geparkt}
          disabledGrund={terminal ? "Item abgeschlossen" : geparkt ? "Bereits geparkt" : null}
          laufend={laufend === "park"}
          label="Parkieren"
          wirkung="Nimmt das Item aus der automatischen Planung (PARKED). Kein Writer arbeitet daran, bis es wieder aktiviert wird."
          onClick={() => void ausfuehren("park", { type: "park", workitemId: item.id })}
        />

        {/* Fortsetzen / Retry — bei FAILED bewusste Bestätigung (Versuchs-Budget!) */}
        <Aktion
          testid="action-resume"
          disabled={terminal || item.queueState === "RUNNING"}
          disabledGrund={terminal ? "Item abgeschlossen" : item.queueState === "RUNNING" ? "Läuft bereits" : null}
          laufend={laufend === "resume"}
          label={fehlerhaft || geparkt ? "Neu anstoßen" : "Fortsetzen"}
          wirkung="Setzt das Item wieder auf startbereit (READY); der Supervisor nimmt es im nächsten Takt auf."
          onClick={() => {
            if (fehlerhaft) {
              setBestaetigung("resume_failed");
            } else {
              void ausfuehren("resume", { type: "resume", workitemId: item.id });
            }
          }}
        />

        {/* Repriorisieren — Auswahl + Apply */}
        <span className="flex items-center gap-1" data-testid="action-priority">
          <select
            className="input px-2 py-1 text-xs"
            value={prioWahl}
            disabled={terminal}
            aria-label="Priorität wählen"
            onChange={(e) => setPrioWahl(e.target.value)}
          >
            {PRIO_WAHL.map((p) => (
              <option key={p.wert} value={p.wert}>
                {p.label}
              </option>
            ))}
          </select>
          <Aktion
            testid="action-priority-apply"
            disabled={terminal || prioWahl === item.priority}
            disabledGrund={terminal ? "Item abgeschlossen" : prioWahl === item.priority ? "Priorität bereits gesetzt" : null}
            laufend={laufend === "priority"}
            label="Priorität setzen"
            wirkung="Ändert die Priorität — der Supervisor plant das Item entsprechend früher/später ein."
            onClick={() => void ausfuehren("priority", { type: "priority", workitemId: item.id, priority: prioWahl })}
          />
        </span>

        {/* Prüfung anfordern (Challenge) — öffnet unabhängiges Prüf-Workitem */}
        {!challengeOffen ? (
          <Aktion
            testid="action-challenge"
            disabled={false}
            laufend={laufend === "challenge"}
            label="Prüfung anfordern"
            wirkung="Beauftragt eine unabhängige evidence-basierte Prüfung (Challenge-Loop mit Verdict — kein Self-Verdict)."
            onClick={() => setChallengeOffen(true)}
          />
        ) : (
          <span className="flex flex-1 flex-wrap items-center gap-1.5" data-testid="action-challenge-form">
            <input
              className="input min-w-[14rem] flex-1 px-2 py-1 text-xs"
              placeholder="Was soll geprüft werden? (Claim formulieren, z. B.: Zustandsanzeige ist veraltet)"
              value={challengeClaim}
              onChange={(e) => setChallengeClaim(e.target.value)}
              aria-label="Prüf-Claim"
            />
            <Aktion
              testid="action-challenge-submit"
              disabled={challengeClaim.trim().length < 3}
              disabledGrund={challengeClaim.trim().length < 3 ? "Claim fehlt" : null}
              laufend={laufend === "challenge"}
              label="Prüfung beauftragen"
              wirkung="Öffnet ein Challenge-Workitem (Priorität Hoch), das den Claim mit Belegen prüft und ein Verdict liefert."
              onClick={() => void ausfuehren("challenge", { type: "challenge", workitemId: item.id, claim: challengeClaim.trim() })}
            />
            <button type="button" className="btn px-2 py-1 text-xs" onClick={() => { setChallengeOffen(false); setChallengeClaim(""); }}>
              Abbrechen
            </button>
          </span>
        )}

        {/* Recovery fortsetzen — nur bei FAILED sichtbar (Server prüft fail-closed) */}
        {fehlerhaft && (
          <Aktion
            testid="action-advance-recovery"
            disabled={false}
            laufend={laufend === "advance_recovery"}
            label="Recovery fortschreiben"
            wirkung="Schreitet die Recovery-Leiter fort (Diagnose → Fix → unabhängige Verifikation). Ohne aktive Recovery lehnt der Server ab."
            onClick={() => void ausfuehren("advance_recovery", { type: "advance_recovery", workitemId: item.id })}
          />
        )}

        {/* Sicher abbrechen — destruktiv: Bestätigung + Grund */}
        <Aktion
          testid="action-safe-cancel"
          gefahr
          disabled={terminal}
          disabledGrund={terminal ? "Item abgeschlossen" : null}
          laufend={laufend === "safe_cancel"}
          label="Sicher abbrechen"
          wirkung="Stoppt einen laufenden Writer sauber (kein Zombie) und parkt das Item mit Vermerk des Grundes."
          onClick={() => setBestaetigung("safe_cancel")}
        />
      </div>

      {/* Gate-Entscheidung — nur wenn das Item wirklich auf den Owner wartet */}
      {wartetAufOwner && (
        <div className={`mt-2 rounded-md border p-2 ${toneClass("waiting")}`} data-testid="owner-gate-decision">
          <div className={`text-xs font-semibold ${toneTextClass("waiting")}`}>Owner-Entscheidung erforderlich</div>
          <p className="mt-0.5 text-[11px] text-jarvis-muted">
            Genehmigen: Item wird wieder startbereit (READY) und läuft automatisch weiter. Ablehnen: Item wird geparkt (Vermerk deiner Notiz).
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Aktion
              testid="action-gate-approve"
              disabled={false}
              laufend={laufend === "owner_decision"}
              label="Genehmigen"
              wirkung="Item wird freigegeben und läuft automatisch weiter."
              onClick={() => void ausfuehren("owner_decision", { type: "owner_decision", workitemId: item.id, decision: "approve" })}
            />
            <Aktion
              testid="action-gate-reject"
              gefahr
              disabled={false}
              laufend={laufend === "owner_decision"}
              label="Ablehnen"
              wirkung="Item wird geparkt; deine Notiz wird als Audit-Beleg gespeichert."
              onClick={() => setBestaetigung("reject")}
            />
          </div>
        </div>
      )}

      {/* Bestätigungs-Flow für destruktive/bewusste Aktionen */}
      {bestaetigung && (
        <div className={`mt-2 rounded-md border p-2.5 ${toneClass("critical")}`} role="alertdialog" aria-label="Aktion bestätigen" data-testid="action-confirm">
          {bestaetigung === "safe_cancel" && (
            <>
              <div className={`text-xs font-semibold ${toneTextClass("critical")}`}>Sicher abbrechen — wirklich?</div>
              <p className="mt-0.5 text-[11px] text-jarvis-muted">
                Ein laufender Writer wird sauber gestoppt und das Item geparkt. Der Grund wird als Audit-Beleg gespeichert.
              </p>
              <input
                className="input mt-1.5 w-full px-2 py-1 text-xs"
                placeholder="Grund (Audit-Beleg, optional)"
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                aria-label="Abbruchsgrund"
              />
              <div className="mt-1.5 flex gap-1.5">
                <Aktion
                  testid="action-safe-cancel-confirm"
                  gefahr
                  disabled={false}
                  laufend={laufend === "safe_cancel"}
                  label="Ja, sicher abbrechen"
                  wirkung=""
                  onClick={() => void ausfuehren("safe_cancel", { type: "safe_cancel", workitemId: item.id, reason: grund.trim() || "cancelled_by_owner" })}
                />
                <button type="button" className="btn px-2 py-1 text-xs" onClick={() => setBestaetigung(null)}>Doch nicht</button>
              </div>
            </>
          )}
          {bestaetigung === "resume_failed" && (
            <>
              <div className={`text-xs font-semibold ${toneTextClass("waiting")}`}>Fehlgeschlagenes Item neu anstoßen?</div>
              <p className="mt-0.5 text-[11px] text-jarvis-muted">
                Ist das Versuchs-Budget erschöpft, gewährt dieses Resume bewusst genau einen weiteren Versuch (am Schema-Cap wird das Budget zurückgesetzt) — als Owner-Entscheidung mit Audit-Beleg.
              </p>
              <div className="mt-1.5 flex gap-1.5">
                <Aktion
                  testid="action-resume-confirm"
                  disabled={false}
                  laufend={laufend === "resume"}
                  label="Ja, neu anstoßen"
                  wirkung=""
                  onClick={() => void ausfuehren("resume", { type: "resume", workitemId: item.id })}
                />
                <button type="button" className="btn px-2 py-1 text-xs" onClick={() => setBestaetigung(null)}>Doch nicht</button>
              </div>
            </>
          )}
          {bestaetigung === "reject" && (
            <>
              <div className={`text-xs font-semibold ${toneTextClass("critical")}`}>Gate ablehnen — wirklich?</div>
              <p className="mt-0.5 text-[11px] text-jarvis-muted">Das Item wird geparkt (owner_rejected). Deine Notiz wird als Audit-Beleg gespeichert.</p>
              <input
                className="input mt-1.5 w-full px-2 py-1 text-xs"
                placeholder="Notiz (warum abgelehnt, optional)"
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                aria-label="Ablehnungs-Notiz"
              />
              <div className="mt-1.5 flex gap-1.5">
                <Aktion
                  testid="action-gate-reject-confirm"
                  gefahr
                  disabled={false}
                  laufend={laufend === "owner_decision"}
                  label="Ja, ablehnen"
                  wirkung=""
                  onClick={() => void ausfuehren("owner_decision", { type: "owner_decision", workitemId: item.id, decision: "reject", note: grund.trim() || undefined })}
                />
                <button type="button" className="btn px-2 py-1 text-xs" onClick={() => setBestaetigung(null)}>Doch nicht</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Ergebnis: Audit-Beleg oder fail-closed Fehler */}
      {receipt && (
        <div className={`mt-2 rounded-md border p-2 text-[11px] ${toneClass("success")}`} role="status" data-testid="action-receipt">
          <strong>✓ {AUDIT_LABEL[receipt.action] ?? receipt.action}.</strong> {receipt.text}
        </div>
      )}
      {fehler && (
        <div className={`mt-2 rounded-md border p-2 text-[11px] ${toneClass("danger")}`} role="alert" data-testid="action-error">
          <strong>✗ Nicht ausgeführt ({fehler.action}).</strong> {fehler.text}
        </div>
      )}
    </div>
  );
}

function auditText(action: string, detail: Record<string, unknown>): string {
  const teile: string[] = [];
  if (typeof detail.state === "string") teile.push(`Zustand jetzt: ${detail.state}`);
  if (typeof detail.decision === "string") teile.push(`Entscheidung: ${detail.decision}`);
  if (typeof detail.priority === "string") teile.push(`Priorität: ${detail.priority}`);
  if (typeof detail.reason === "string") teile.push(`Grund: ${detail.reason}`);
  if (typeof detail.challengeWorkitemId === "string") teile.push(`Prüf-Workitem: ${detail.challengeWorkitemId}`);
  teile.push("Audit-Eintrag in der Item-Evidence (Technische Details / Ereignisse).");
  return teile.join(" · ");
}

function Aktion({
  testid,
  label,
  wirkung,
  disabled,
  disabledGrund = null,
  laufend,
  gefahr,
  onClick,
}: {
  testid: string;
  label: string;
  wirkung: string;
  disabled: boolean;
  disabledGrund?: string | null;
  laufend: boolean;
  gefahr?: boolean;
  onClick: () => void;
}): JSX.Element {  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled || laufend}
      title={[wirkung, disabled && disabledGrund ? `Nicht möglich: ${disabledGrund}` : null].filter(Boolean).join("\n\n") || undefined}
      aria-disabled={disabled || undefined}
      onClick={onClick}
      className={`btn min-h-11 px-2.5 py-1.5 text-xs font-medium ${
        gefahr ? `${toneClass("critical")} hover:brightness-110` : ""
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {laufend ? "…" : label}
    </button>
  );
}
